import React, {useCallback, useMemo} from 'react'
import styled from 'styled-components'
import {getBorder, getBoxShadow, getCurrencyValue, useDidMount} from 'utils'
import {useTranslation} from 'i18n'
import {GiftShopCheckoutScreenParams} from 'pages'
import {WindowModeType} from 'types'
import {useWindowMode} from 'windows'
import convertUnit from 'lib/unit'
import {useSelector} from 'lib/redux'
import {Button, Paragraph, PaymentModal} from 'common/components'
import {GiftShopCartSummaryProps} from './GiftShopCartSummaryProps'

interface StyledContainerProps {
  mode: WindowModeType
}

const StyledContainer = styled.div<StyledContainerProps>`
  ${({theme, mode}) => ({
    backgroundColor: theme.white_1,
    boxShadow: mode === 'website' ? getBoxShadow(theme) : undefined,
    borderRadius: mode === 'website' ? convertUnit(16) : undefined,
    borderTop:
      mode === 'website' ? undefined : getBorder(1, 'solid', theme.white_4),
    padding: `${convertUnit(mode === 'website' ? 20 : 12)} ${convertUnit(20)}`,
  })}
  box-sizing: border-box;
  width: 100%;
`

const StyledSectionPrice = styled.div`
  display: flex;
  flex-direction: row;
`

const StyledPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const StyledTitle = styled(Paragraph)`
  margin-bottom: ${convertUnit(20)};
`

const StyledPrice = styled(Paragraph)`
  margin-top: ${convertUnit(5)};
`

const StyledButton = styled(Button)`
  width: auto;
`

const StyledBottomButton = styled(StyledButton)`
  width: 100%;
  margin-top: ${convertUnit(20)};
`

export default function GiftShopCartSummary({
  select,
  totalPrice,
  stateToggle,
  priceLoaded,
  onContinue,
}: GiftShopCartSummaryProps) {
  const userInfo = useSelector('user')!
  const mode = useWindowMode()
  const {translate} = useTranslation()
  const [toggle, setToggle] = stateToggle
  const disabled = useMemo(() => !priceLoaded || select.length === 0, [
    priceLoaded,
    select.length,
  ])

  const params = useMemo<GiftShopCheckoutScreenParams>(
    () => ({data: select, userInfo, platform: 'web'}),
    [select, userInfo],
  )

  const handleRenderTitle = useMemo(
    () =>
      mode === 'website' && (
        <StyledTitle fontSize="l" fontWeight="bold">
          {translate('giftShop:cartSummaryTitle')}
        </StyledTitle>
      ),
    [mode, translate],
  )

  const handleRenderPrice = useMemo(
    () => (
      <StyledPriceContainer>
        <Paragraph fontWeight="medium" color="gray_5">
          {translate('giftShop:cartSummaryTotalPriceTitle', {
            count: select.length,
          })}
        </Paragraph>
        <StyledPrice
          fontSize="l"
          fontWeight="bold"
          color={disabled ? 'black' : 'primary_5'}>
          {getCurrencyValue(disabled ? 0 : totalPrice)}
        </StyledPrice>
      </StyledPriceContainer>
    ),
    [disabled, select.length, totalPrice, translate],
  )

  const handleRenderButton = useMemo(
    () =>
      mode !== 'website' && (
        <StyledButton
          disabled={disabled}
          label={translate('global:continue')}
          onClick={onContinue}
        />
      ),
    [disabled, mode, onContinue, translate],
  )

  const handleRenderBottomButton = useMemo(
    () =>
      mode === 'website' && (
        <StyledBottomButton
          disabled={disabled}
          label={translate('global:continue')}
          onClick={onContinue}
        />
      ),
    [disabled, mode, onContinue, translate],
  )

  const handleToggle = useCallback(() => setToggle((previous) => !previous), [
    setToggle,
  ])

  useDidMount(() => {
    ;(window as any).getData = () => select
  })

  return (
    <StyledContainer mode={mode}>
      {handleRenderTitle}
      <StyledSectionPrice>
        {handleRenderPrice}
        {handleRenderButton}
      </StyledSectionPrice>
      {handleRenderBottomButton}
      <PaymentModal
        toggleModal={handleToggle}
        visible={toggle}
        params={params}
      />
    </StyledContainer>
  )
}
